import { useMemo, useRef, useEffect, useState } from "react";
import { useType } from "../../context/TypeContext";
import { useLanguage } from "../../context/LanguageContext";
import { fullPegaConfig } from "../config/pegaEmbedConfig";
import { useEnv } from "../../context/EnvironmentContext";
import { PegaEmbedAttributes } from "../../global";

interface PegaEmbedProps {
  accessToken: string;
  staticContentServer: string;
}

type PegaWebEmbed = HTMLElement & PegaEmbedAttributes;

const PegaEmbed: React.FC<PegaEmbedProps> = ({ accessToken, staticContentServer }) => {
  const { type, variant } = useType() as {
    type: keyof typeof pegaConfig;
    variant: string;
  };

  const webCompRef = useRef<PegaWebEmbed>(null);
  const { env } = useEnv();
  const { isSwedish } = useLanguage();

  const fiTheme = `{
    "base": {
      "palette": {
        "brand-primary": "#006a7d",
        "interactive": "#196a7d",
        "border-line": "#adb8bf"
      }
    },
    "components": {
      "avatar": {
        "background-color": "#6e2b62"
      },
      "form-control": {
        "border-color": "#939393",
        "border-radius": 0.5
      },
      "link": {
        "color": "#196a7d"
      },
      "button": {
        "border-radius": 9999
      }
    }
  }`;

  // Default configuration for the Pega embed component.
  let globalDefaults = {
    appAlias: env.PEGA_APP,
    pegaServerUrl: env.BACKEND_URL,
    autoReauth: "false",
    grantType: "none",
    theme: fiTheme,
  };

  // Central configuration with sub-structures for common parameters and variants.
  const pegaConfig = {
    defer: {
      common: {},
      variants: {
        default: {
          action: ""
        }
      }
    },
    open: {
      common: {
        action: "openPage",
        pageClass: "Data-Portal",
        casePage: "full",
      },
      variants: {
        pyWorklist: {
          pageID: "pyWorklist",
        },
        CandidateHomePage: {
          pageID: "CandidateHome",
          pageClass: "FI-Authorisation-UIPages",
        },
        AgentHomePage: {
          pageID: "AgentHome",
          pageClass: "FI-Authorisation-UIPages",
        },

        HomePage: {
          pageID: "TasksAndActiveCases",
          pageClass: "FI-Tilda-UIPages",
        },
        ActiveCasesPage: {
          pageID: "ActiveCases",
          pageClass: "FI-Authorisation-UIPages",
        },
        ClosedCasesPage: {
          pageID: "ClosedCases",
          pageClass: "FI-Authorisation-UIPages",
        },
      },
    },
  };

  const currentConfig: fullPegaConfig = useMemo(() => {
    if (!type || !variant || !(type in pegaConfig)) {
      return {
        action: "",
        pegaServerUrl: "",
      };
    }

    // Merge the common parameters with the variant-specific ones.
    const variantConfig =
      pegaConfig[type].variants[
      variant as keyof (typeof pegaConfig)[typeof type]["variants"]
      ];
    return {
      ...globalDefaults,
      ...pegaConfig[type].common,
      ...(variantConfig ? variantConfig : {}),
    };

  }, [type, variant]);

  const additionalHeaders = isSwedish ? { "x-locale": "sv_SE" } : { "x-locale": "en_GB" };

  const [prevId, setPrevId] = useState(additionalHeaders["x-locale"]);

  const [webComponentKey, setWebComponentKey] = useState(0);

  if (additionalHeaders["x-locale"] !== prevId) {
    setPrevId(additionalHeaders["x-locale"]); // Sync the tracker
    setWebComponentKey((prevKey) => prevKey + 1); // Change the key
  }

  if (webCompRef.current) {
    if (webCompRef.current.locale != additionalHeaders["x-locale"]) {
      window.PCore?.getRestClient().getHeaderProcessor().registerHeader("X-Locale", additionalHeaders["x-locale"]);
    }
  }

  const handleEmbedReady = () => {
    if (window.PCore) {
      window.PCore.getEnvironmentInfo().isWebEmbed = true;
    }
  };

  useEffect(() => {
    const el: PegaWebEmbed | null = document.querySelector('pega-embed');

    if (el && el.reload && el.bootstrapComplete) el.reload();
  }, [variant]); // The hook "watches" this variable

  return (
    <pega-embed
      // key={`${type}`} // Remount when type or variant changes.
      // key={`${type}-${variant}`} // Remount when type or variant changes.
      ref={webCompRef}
      key={webComponentKey}
      customCSS="/custom-overrides.css"
      /* General */
      onembedready={handleEmbedReady}
      id="theEmbed"
      autoReauth={currentConfig.autoReauth}
      /* Action */
      action={currentConfig.action}
      pageID={currentConfig.pageID}
      pageClass={currentConfig.pageClass}
      // caseTypeID={currentConfig.caseTypeID}
      casePage={currentConfig.casePage}
      /* Configuration */
      appAlias={currentConfig.appAlias}
      pegaServerUrl={currentConfig.pegaServerUrl}
      staticContentUrl={staticContentServer}
      // BUG 158811 - changed from en_EN to en_GB
      locale={isSwedish ? "sv_SE" : "en_GB"}
      additionalHeaders={additionalHeaders}
      /* Authentication */
      authHeader={`Bearer ${accessToken}`}
      grantType={currentConfig.grantType}
      /* Theme */
      theme={currentConfig.theme}
    ></pega-embed>
  );
}

export default PegaEmbed;
